import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomerForm from '../components/CustomerForm.jsx';
import Badge, { PosBadge, StatusBadge, OpenStatusBadge } from '../components/Badge.jsx';
import QuoteDialog from '../components/QuoteDialog.jsx';
import QuotesTab from '../components/QuotesTab.jsx';
import BullochConfigDialog from '../components/BullochConfigDialog.jsx';
import BullochEmailParseDialog from '../components/BullochEmailParseDialog.jsx';
import SiteForm from '../components/SiteForm.jsx';
import CredentialForm from '../components/CredentialForm.jsx';
import SftpFolderBrowser from '../components/SftpFolderBrowser.jsx';
import SiteSubscriptionPanel from '../components/SiteSubscriptionPanel.jsx';
import PriceIncreaseEmailDialog from './../components/PriceIncreaseEmailDialog.jsx';
import StatementDialog from '../components/StatementDialog.jsx';
import DateInput from '../components/DateInput.jsx';
import ContactsSection from '../components/ContactsSection.jsx';
import PackageSelector from '../components/PackageSelector.jsx';
import OutreachStageDot from '../components/OutreachStageDot.jsx';
import CustomerRenewalsTab from '../components/CustomerRenewalsTab.jsx';
import BackOfficeTab from '../components/BackOfficeTab.jsx';
import NewTicketModal from '../components/NewTicketModal.jsx';
import SendEmailDialog from '../components/SendEmailDialog.jsx';
import XsiteioOnboardingDialog from '../components/XsiteioOnboardingDialog.jsx';
import CopyMeetingInfoButton from '../components/CopyMeetingInfoButton.jsx';
import EmailOpenBadge from '../components/EmailOpenBadge.jsx';
import PhoneLink from '../components/PhoneLink.jsx';
import CallsTab from '../components/CallsTab.jsx';
import EmailsTab from '../components/EmailsTab.jsx';
import PaymentsTab from '../components/PaymentsTab.jsx';
import SiteEmails from '../components/SiteEmails.jsx';
import ChiBadge from '../components/ChiBadge.jsx';
import SupportTeamPanel from '../components/SupportTeamPanel.jsx';
import EmailPreviewModal from '../components/EmailPreviewModal.jsx';
import { useAuth } from '../context/AuthContext.jsx';

// The customer record is the billing contact / account overview. All
// operational detail (quotes, Bulloch requests, subscriptions, SFTP
// configurations) lives on the per-site rows of the Sites tab.
//
// The Contacts list lives inline at the bottom of the Details tab now
// — below the contact card itself — so the whole billing surface is
// visible in one place. No separate Contacts tab.
const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'sites', label: 'Sites' },
  { id: 'subscriptions', label: 'Subscriptions' },
  // Back Office tab hidden 2026-06-09 (Dave). The data it surfaced
  // (linked Bulloch Back Office client, sites, subscription rows) is
  // now covered by the local Subscriptions tab + xsiteio integration.
  // Kept the component + route + reachable conditional render below
  // so we can put it back instantly if a need turns up. Delete the
  // component + backofficeController + routes after a month or two of
  // nobody missing it.
  // { id: 'backoffice', label: 'Back Office' },
  { id: 'notes', label: 'Notes' },
  { id: 'calls', label: 'Calls' },
  { id: 'emails', label: 'Emails' },
  { id: 'payments', label: 'Payments' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'documents', label: 'Documents' }
];

// Sum a single subscription row's active modules. Used by the
// header-stats reducer and the per-site subtotal in the table.
// Activeness is renewal_date-driven (the canonical rule across the
// app): a module contributes to the total iff its renewal date is
// set. The legacy `*_active` columns + `*_addon_price` columns are
// no longer read.
const _MODULES_FIELDS = [
  { renewal: 'live_transactions_renewal_date', price: 'live_transactions_annual' },
  { renewal: 'price_book_renewal_date',        price: 'price_book_annual' },
  { renewal: 'fuel_manager_renewal_date',      price: 'fuel_manager_annual' },
  { renewal: 'charge_renewal_date',            price: 'charge_annual' },
  { renewal: 'tx_exempt_renewal_date',         price: 'tx_exempt_annual' },
  { renewal: 'car_wash_renewal_date',          price: 'car_wash_annual' },
  { renewal: 'adv_report_renewal_date',        price: 'adv_report_annual' },
  { renewal: 'fuel_price_renewal_date',        price: 'fuel_price_annual' },
  { renewal: 'case_break_renewal_date',        price: 'case_break_annual' },
  { renewal: 'dealer_comm_renewal_date',       price: 'dealer_comm_annual' },
  { renewal: 'time_rest_renewal_date',         price: 'time_rest_annual' },
  { renewal: 'sft_file_renewal_date',          price: 'sft_file_annual' },
  { renewal: 'private_label_renewal_date',     price: 'private_label_annual' },
  { renewal: 'imperial_import_renewal_date',   price: 'imperial_import_annual' },
  { renewal: 'fuel_grade_report_renewal_date', price: 'fuel_grade_report_annual' },
  { renewal: 'inventory_control_renewal_date', price: 'inventory_control_annual' }
];
const _RENEWAL_FIELDS = _MODULES_FIELDS.map((m) => m.renewal);

function siteAnnualTotal(sub) {
  if (!sub) return 0;
  let total = 0;
  for (const m of _MODULES_FIELDS) {
    if (!sub[m.renewal]) continue;
    const v = Number(sub[m.price]);
    if (Number.isFinite(v)) total += v;
  }
  return Math.round(total * 100) / 100;
}

// Earliest renewal across every ACTIVE site's subscription → drives
// the Renewal Status badge on the header. Inactive sites are excluded
// deliberately: a customer with only inactive sites shouldn't read
// as "Active" just because a stale renewal_date sits on their
// long-cancelled subscription (Dave hit this on Esso Traverse Bay
// 2026-07-09 -- 1 inactive site + renewal_date 2027-06-15 was
// showing green ACTIVE).
//
// Buckets:
//   No active sites: customer has 0 active sites -> grey 'Inactive'
//   No renewal on file: at least one active site but none has a
//                       renewal_date -> grey em-dash
//   Overdue: earliest renewal on an active site is in the past
//   Near:    earliest renewal within 180 days
//   Active:  earliest renewal further out
function customerRenewalStatus(subscriptionsBySite, sites) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const activeSiteIds = new Set(
    (sites || [])
      .filter((s) => String(s.status || '').toLowerCase() === 'active')
      .map((s) => s.id)
  );
  if (activeSiteIds.size === 0) {
    return { label: 'Inactive', tone: 'grey', earliest: null };
  }
  let earliest = null;
  for (const [siteId, sub] of Object.entries(subscriptionsBySite || {})) {
    if (!sub) continue;
    if (!activeSiteIds.has(Number(siteId))) continue;
    for (const f of _RENEWAL_FIELDS) {
      const v = sub[f];
      if (!v) continue;
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) continue;
      if (earliest == null || d < earliest) earliest = d;
    }
  }
  if (!earliest) return { label: '—', tone: 'grey', earliest: null };
  const days = Math.round((earliest - today) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: 'Overdue', tone: 'red',   earliest, days };
  if (days <= 180) return { label: 'Near',    tone: 'amber', earliest, days };
  return                   { label: 'Active',  tone: 'green', earliest, days };
}

function moneyFmt(n) {
  if (!Number.isFinite(n)) return '$0.00';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// First Contact label — prefers the explicit first_contact_date on
// customer_details (real-world date of first contact). Falls back to
// contacts.created_at (DB insert time) only when first_contact_date
// hasn't been set, so legacy records still show something.
function firstContactLabel(c) {
  const iso = c?.first_contact_date || c?.created_at;
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// Does this site subscribe to Fuel Manager? Canonical rule across the
// app: a module subscription is "active" iff its renewal date is set
// (see _MODULES_FIELDS above). Returns 'subscribed', 'unsubscribed',
// or 'unknown' when the subscription record itself is missing (which
// in practice happens on freshly-created sites that haven't had their
// subscriptions saved yet -- treat as unknown rather than guess).
// Used to render the Last Fuel & Financial Done cell -- non-
// subscribers shouldn't look like "broken subscribers" because they
// never bought the module. Dave 2026-06-24.
function fmSubscriptionState(subscription) {
  if (!subscription) return 'unknown';
  return subscription.fuel_manager_renewal_date ? 'subscribed' : 'unsubscribed';
}

// dd-mm-yyyy formatter for fields that are conceptually dates, not
// wall-clock timestamps (e.g. the Last Fuel & Financial Done shift
// date sourced from xsiteio). Dave 2026-06-24 preferred format.
function formatDDMMYYYY(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function formatTime(value) {
  if (!value) return '';
  const s = String(value);
  const match = s.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : s;
}

// Relative-time formatter for xsiteio activity timestamps. Short and
// scannable: "3d ago", "12h ago", "8m ago", "just now". Anything older
// than ~60 days falls back to a date so it doesn't show "240d ago".
function formatRelative(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = Date.now() - d.getTime();
  if (ms < 0) return d.toISOString().slice(0, 10);
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 60) return `${day}d ago`;
  return d.toISOString().slice(0, 10);
}

// Map an xsiteio timestamp to a status colour for the activity dot.
// Thresholds chosen to match the "Quiet Sites" panel default: anything
// older than 14 days is a concern; older than 30 is bad. Null = grey
// (we just haven't synced this site yet, not necessarily their fault).
function xsiteioTone(value) {
  if (!value) return 'var(--color-inactive)';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'var(--color-inactive)';
  const days = (Date.now() - d.getTime()) / 86400000;
  if (days <= 7)  return 'var(--color-good)';
  if (days <= 14) return 'var(--color-warn)';
  return 'var(--color-bad)';
}

// Last Shift tone -- tighter thresholds than general xsiteio freshness
// because shift files are daily-cadence. Anything more than 2 days old
// means we should check with Bulloch that the SFTP config is still
// correct. Dave 2026-07-09: green <=2d, amber 3-5d, red >5d.
function lastShiftTone(value) {
  if (!value) return 'var(--color-inactive)';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'var(--color-inactive)';
  const days = (Date.now() - d.getTime()) / 86400000;
  if (days <= 2) return 'var(--color-good)';
  if (days <= 5) return 'var(--color-warn)';
  return 'var(--color-bad)';
}

function noteTone(type) {
  switch (type) {
    case 'Phone': return 'blue';
    case 'Email': return 'purple';
    case 'Meeting': return 'green';
    case 'Demo': return 'orange';
    default: return 'grey';
  }
}

function displayName(customer) {
  if (!customer) return 'Customer';
  if (customer.company) return customer.company;
  const parts = [customer.first_name, customer.last_name].filter(Boolean);
  return parts.join(' ') || `Customer #${customer.id}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Honour ?tab=sites on the URL so the Customers-list Enter-to-jump
  // (and Activity Log site links, future deep-links, etc.) can land
  // directly on the Sites tab instead of forcing a second click.
  const [searchParams] = useSearchParams();
  const initialTab = ['details','sites','subscriptions','backoffice','notes','calls','emails','payments','quotes','documents']
    .includes(searchParams.get('tab')) ? searchParams.get('tab') : 'details';
  const [active, setActive] = useState(initialTab);
  // site_number param tells the Sites tab to scroll/highlight a
  // specific row. Read once on mount; the Sites tab implementation
  // can pick it up via the prop / DOM if it wants to scroll into view.
  const focusSiteNumber = searchParams.get('site_number') || null;

  async function handleDeleteCustomer() {
    const name = data?.customer?.company || 'this customer';
    const siteCount = data?.sites?.length || 0;
    const msg = siteCount > 0
      ? `Delete ${name} and all ${siteCount} of its site${siteCount === 1 ? '' : 's'}? This also removes its notes, documents, credentials, and subscriptions. This cannot be undone.`
      : `Delete ${name}? This also removes its notes and documents. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/customers/${id}`);
      navigate('/customers');
    } catch (err) {
      setDeleting(false);
      window.alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  }

  // Claim this customer for the logged-in user. Allow-re-steal model
  // (Dave 2026-06-09): no confirmation prompt, just one click. If
  // another rep already owns it, this silently overwrites -- that's
  // intentional. Refreshes the page data so the owner chip in the
  // header updates immediately.
  async function handleClaim() {
    setClaiming(true);
    try {
      await axios.post(`/api/customers/${id}/claim`);
      await load();
    } catch (err) {
      window.alert('Claim failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setClaiming(false);
    }
  }
  const [saveMessage, setSaveMessage] = useState(null);
  // Generate Quote + Bulloch Config Request are per-SITE now and live
  // on each Site row in the Sites tab. The customer-level header keeps
  // only Generate Account Statement (a billing-overview action).
  const [statementOpen, setStatementOpen] = useState(false);
  // Send Email dialog — account suspended / reactivation outreach
  // plus any future Customers-category templates we seed.
  const [sendEmailOpen, setSendEmailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/customers/${id}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(form) {
    await axios.put(`/api/customers/${id}`, form);
    setSaveMessage('Saved.');
    await load();
    setTimeout(() => setSaveMessage(null), 2500);
  }

  if (loading && !data) {
    return (
      <section className="page">
        <p>Loading…</p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="page">
        <div className="page-header">
          <h2>Customer</h2>
          <Link to="/customers" className="btn-secondary">← Back to Customers</Link>
        </div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const customer = data.customer;
  const location = [customer.city, customer.province_state].filter(Boolean).join(', ');
  const contactName = [customer.first_name, customer.last_name].filter(Boolean).join(' ');

  // Account-overview stats derived from the per-site subscriptions
  // payload — these populate the new header strip alongside the
  // billing contact line. Recompute each render so a save inside the
  // Sites tab is reflected without a page reload.
  const subsBySite = data.subscriptions_by_site || {};
  const totalAnnual = data.sites.reduce(
    (acc, s) => acc + siteAnnualTotal(subsBySite[s.id]),
    0
  );
  const renewalStatus = customerRenewalStatus(subsBySite, data.sites);
  const customerSince = firstContactLabel(customer);

  // ─── Header-strip data (Dave 2026-05-31) ─────────────────────────
  // Contact cadence, location summary, recent-activity preview - so
  // the top of the customer page tells you what you need before you
  // reach out. Computed each render so saves elsewhere in the page
  // (notes, sites) are reflected without a reload.
  const todayMs = Date.now();
  const dayMs = 86400000;
  function dayDiff(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.round((todayMs - t) / dayMs);
  }
  const lastContactDays = dayDiff(customer.last_contact);
  const nextContactDays = dayDiff(customer.next_contact); // >0 = past (overdue), <0 = future
  const uniqueCities = Array.from(
    new Set(data.sites.map((s) => s.city).filter(Boolean))
  ).sort();
  const cityPreview = uniqueCities.length === 0
    ? null
    : uniqueCities.slice(0, 3).join(', ') +
      (uniqueCities.length > 3 ? ` +${uniqueCities.length - 3} more` : '');
  const lastNote = (data.notes || [])[0];
  function shortText(s, max = 60) {
    if (!s) return '';
    const v = String(s).replace(/\s+/g, ' ').trim();
    return v.length > max ? v.slice(0, max - 1) + '…' : v;
  }
  const lastEmail = (data.documents || [])
    .filter((d) => d.type === 'Email')
    .sort((a, b) => {
      const at = new Date(a.sent_at || a.created_at).getTime();
      const bt = new Date(b.sent_at || b.created_at).getTime();
      return bt - at;
    })[0];

  return (
    <section className="page">
      <div className="page-header">
        <Link to="/customers" className="btn-secondary">← Back to Customers</Link>
      </div>

      <StatementDialog
        open={statementOpen}
        record={customer}
        onClose={() => setStatementOpen(false)}
        onSent={load}
      />

      <SendEmailDialog
        open={sendEmailOpen}
        record={customer}
        recordType="customer"
        sites={data.sites}
        onClose={() => setSendEmailOpen(false)}
        onSent={load}
      />

      <div className="record-header customer-overview-header">
        <div className="record-title" style={{ flex: 1 }}>
          <h2 style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <OutreachStageDot stage={customer.outreach_stage} size={12} />
            {displayName(customer)}
            {/* CHI badge -- latest snapshot, colored red/amber/green */}
            <ChiBadge score={customer.chi_score} size="lg" />
          </h2>
          <div className="record-meta">
            {contactName && <span>{contactName}</span>}
            {contactName && location && <span className="sep" />}
            {location && <span>{location}</span>}
            {/* Phone Cell — promoted to the header meta line so Dave
                doesn't have to scroll to the form for the number he
                uses most. Click-to-call via tel:. Phone Main next,
                also click-to-call. Email is mailto:. Dave 2026-06-26. */}
            {customer.phone_cell && <><span className="sep" /><PhoneLink number={customer.phone_cell} /></>}
            {customer.phone_main && <><span className="sep" /><PhoneLink number={customer.phone_main} /></>}
            {customer.email1 && <><span className="sep" /><a href={`mailto:${customer.email1}`} className="cell-mono" style={{ color: 'inherit' }}>{customer.email1}</a></>}
            {/* Owner chip -- shows who's currently working this
                customer for outreach. Unclaimed customers show a muted
                "Unclaimed" hint so Zoe/Kaden can spot pickable rows
                from the meta line at a glance. */}
            <span className="sep" />
            {customer.owner_name ? (
              <span>Owner: <strong>{customer.owner_name}</strong></span>
            ) : (
              <span style={{ color: 'var(--color-text-faint)' }}>Unclaimed</span>
            )}
          </div>
        </div>
        <div className="record-actions">
          {/* Copy Meeting Info -- 3-line clipboard copy (site / contact /
              host) for pasting into Teams/Zoom/agenda. Dave 2026-06-30. */}
          <CopyMeetingInfoButton record={customer} user={currentUser} kind="customer" contactId={id} onLogged={load} />
          <PackageSelector
            contactId={customer.id}
            recordType="customer"
            value={customer.package}
            siteCount={data.sites.length}
            onApplied={load}
          />
          {/* Claim button -- one click, no confirmation. Hidden when
              the current user is already the owner (no point re-
              claiming yourself). Re-steal allowed so it's enabled
              whether the customer is unowned or owned by someone else. */}
          {customer.owner_id !== currentUser?.id && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClaim}
              disabled={claiming}
              title={customer.owner_id
                ? `Reassign from ${customer.owner_name || 'current owner'} to you`
                : 'Take ownership of this customer'}
            >
              {claiming ? 'Claiming…' : (customer.owner_id ? 'Claim from ' + (customer.owner_name?.split(' ')[0] || 'owner') : 'Claim')}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={() => setSendEmailOpen(true)}>
            Send Email
          </button>
          {/* Mailing label opens in a new tab and auto-fires the
              browser print dialog. Dave 2026-06-22 -- replaces the
              Maximizer "Print Mailing Label" feature for shipping
              packages to customers. */}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => window.open(`/customers/${id}/mailing-label`, '_blank', 'noopener')}
            title="Open printable mailing label in a new tab"
          >
            Print Mailing Label
          </button>
          <button type="button" className="btn-primary" onClick={() => setStatementOpen(true)}>
            Generate Account Statement
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleDeleteCustomer}
            disabled={deleting}
            title="Delete this customer and all its sites"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Account overview strip — billing-only summary that lets David
          see at a glance how big this customer is and when their
          earliest renewal is. Per-site actions (quote, bulloch) live
          on each site row inside the Sites tab. */}
      <div className="customer-overview-stats">
        <div className="overview-stat">
          <span className="overview-stat-label">First Contact</span>
          <span className="overview-stat-value">{customerSince}</span>
        </div>
        <div className="overview-stat">
          <span className="overview-stat-label">Sites</span>
          {/* Show total + active/inactive split so chains like Centex
              Petroleum (where some of the 85 sites are inactive) read
              clearly at a glance. Dave 2026-06-12. */}
          {(() => {
            const total = data.sites.length;
            const active = data.sites.filter((s) => String(s.status || '').toLowerCase() === 'active').length;
            const inactive = data.sites.filter((s) => String(s.status || '').toLowerCase() === 'inactive').length;
            const other = total - active - inactive; // Pending etc.
            const parts = [];
            if (active   > 0) parts.push(`${active} active`);
            if (inactive > 0) parts.push(`${inactive} inactive`);
            if (other    > 0) parts.push(`${other} other`);
            return (
              <>
                <span className="overview-stat-value overview-stat-badge">{total}</span>
                {parts.length > 0 && (
                  <span className="overview-stat-sub" style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                    {parts.join(' · ')}
                  </span>
                )}
              </>
            );
          })()}
          {cityPreview && (
            <span className="overview-stat-sub" style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
              {cityPreview}
            </span>
          )}
        </div>
        <div className="overview-stat">
          <span className="overview-stat-label">Total Annual Value</span>
          <span className="overview-stat-value cell-mono">{moneyFmt(totalAnnual)}</span>
        </div>
        <div className="overview-stat">
          <span className="overview-stat-label">Renewal Status</span>
          <span className="overview-stat-value">
            <Badge tone={renewalStatus.tone}>{renewalStatus.label}</Badge>
            {renewalStatus.earliest && (
              <span className="overview-stat-sub cell-mono">
                {renewalStatus.earliest.toISOString().slice(0, 10)}
              </span>
            )}
          </span>
        </div>

        <div className="overview-stat">
          <span className="overview-stat-label">Contact Cadence</span>
          <span className="overview-stat-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <span style={{ fontSize: 'var(--text-body)' }}>
              {lastContactDays == null
                ? <span style={{ color: 'var(--color-text-faint)' }}>No contact yet</span>
                : <>Last: <strong>{lastContactDays === 0 ? 'today' : `${lastContactDays}d ago`}</strong></>}
            </span>
            {nextContactDays != null && (
              <span style={{
                fontSize: 'var(--text-small)',
                color: nextContactDays > 0 ? 'var(--color-warn)' : 'var(--color-text-dim)'
              }}>
                {nextContactDays > 0
                  ? `Next: overdue by ${nextContactDays}d`
                  : nextContactDays === 0
                    ? 'Next: today'
                    : `Next: in ${-nextContactDays}d`}
              </span>
            )}
          </span>
        </div>

        <div className="overview-stat" style={{ minWidth: 260, flex: '1 1 260px' }}>
          <span className="overview-stat-label">Recent Activity</span>
          <span className="overview-stat-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            {lastNote ? (
              <span style={{ fontSize: 'var(--text-small)' }}>
                <strong>{lastNote.type || 'Note'}</strong>
                {lastNote.note_date && (
                  <span className="cell-mono" style={{ color: 'var(--color-text-dim)', marginLeft: 6 }}>
                    {formatDate(lastNote.note_date)}
                  </span>
                )}
                {lastNote.text && (
                  <span style={{ display: 'block', color: 'var(--color-text-dim)' }}>
                    {shortText(lastNote.text)}
                  </span>
                )}
              </span>
            ) : (
              <span style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-faint)' }}>No notes yet</span>
            )}
            {lastEmail && (
              <span style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                Last email: <span className="cell-mono">{formatDate(lastEmail.sent_at || lastEmail.created_at)}</span>
                {lastEmail.opened_at && <span style={{ color: 'var(--color-good)', marginLeft: 6 }}>· opened</span>}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Support Team strip moved down to each site's expanded row --
          multi-brand customers (e.g. one Esso site + one Chevron site)
          need a per-site group. See SitesTab. */}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab${active === t.id ? ' tab-active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
            {t.id === 'sites' && data.sites.length > 0 && (() => {
              // Tab badge -- show active count when there are inactive
              // sites (so the badge matches the working set), otherwise
              // just the total. Tooltip carries the full breakdown.
              const active = data.sites.filter((s) => String(s.status || '').toLowerCase() === 'active').length;
              const inactive = data.sites.filter((s) => String(s.status || '').toLowerCase() === 'inactive').length;
              const showSplit = inactive > 0;
              return (
                <span
                  className="tab-count"
                  title={showSplit ? `${active} active · ${inactive} inactive · ${data.sites.length} total` : `${data.sites.length} total`}
                >
                  {showSplit ? `${active}/${data.sites.length}` : data.sites.length}
                </span>
              );
            })()}
            {t.id === 'notes' && data.notes.length > 0 && <span className="tab-count">{data.notes.length}</span>}
            {t.id === 'quotes' && data.documents.filter((d) => (d.type || '').toLowerCase() === 'quote').length > 0 && <span className="tab-count">{data.documents.filter((d) => (d.type || '').toLowerCase() === 'quote').length}</span>}
            {t.id === 'documents' && data.documents.length > 0 && <span className="tab-count">{data.documents.length}</span>}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {saveMessage && <p className="save-message">{saveMessage}</p>}

        {active === 'details' && (
          <>
            <CustomerForm
              key={customer.id}
              initial={customer}
              onSubmit={handleUpdate}
              submitLabel="Save Changes"
            />
            {/* Contacts section sits inline below the contact card —
                one detail surface for the billing / account view. The
                same /api/sub-contacts endpoints power the inline list. */}
            <ContactsSection
              contactId={customer.id}
              items={data.sub_contacts || []}
              onChanged={load}
            />
          </>
        )}

        {active === 'sites' && (
          <SitesTab
            customer={customer}
            customerId={customer.id}
            sites={data.sites}
            credentials={data.site_credentials || []}
            subscriptionsBySite={data.subscriptions_by_site || {}}
            configurations={data.configurations || []}
            focusSiteNumber={focusSiteNumber}
            onSaved={load}
          />
        )}
        {active === 'subscriptions' && <CustomerRenewalsTab contactId={customer.id} />}
        {active === 'backoffice' && (
          <BackOfficeTab
            customerId={customer.id}
            customerName={customer.company || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
            backofficeClientId={customer.backoffice_client_id || null}
            onLinked={load}
          />
        )}
        {active === 'notes' && <NotesTab customerId={id} notes={data.notes} onAdded={load} />}
        {active === 'calls' && <CallsTab contactId={customer.id} />}
        {active === 'emails' && <EmailsTab contactId={customer.id} />}
        {active === 'payments' && <PaymentsTab contactId={customer.id} />}
        {active === 'quotes' && <QuotesTab items={data.documents} onChanged={load} />}
        {active === 'documents' && <DocumentsTab items={data.documents} />}
      </div>
    </section>
  );
}

// Auto-detect a label from the username suffix — mirrors
// inboundEmailParser.labelForUsername on the server. Kept local to this
// component so a refresh after a save shows the right label even before
// the next round-trip writes it.
function autoLabel(username) {
  if (!username) return '';
  const lower = String(username).toLowerCase();
  if (lower.endsWith('-bk'))  return 'Esso';
  if (lower.endsWith('-pcp')) return 'PetroCan';
  return '';
}

function SitesTab({ customer, customerId, sites, credentials, subscriptionsBySite, configurations, focusSiteNumber, onSaved }) {
  const { user } = useAuth();
  const [ticketSite, setTicketSite] = useState(null); // site for the + Ticket modal
  const [query, setQuery] = useState('');              // real-time site search
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);            // site | null
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [credModalSite, setCredModalSite] = useState(null);
  const [credModalCred, setCredModalCred] = useState(null); // editing this credential, or null = add new
  const [testingCredId, setTestingCredId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  // Which site rows are expanded. Clicking a row toggles it.
  // When focusSiteNumber is passed via the URL (?site_number=…), we
  // auto-expand that row + scroll to it + flash a highlight on mount.
  const [expandedSiteIds, setExpandedSiteIds] = useState(() => {
    if (!focusSiteNumber) return new Set();
    const target = String(focusSiteNumber).toLowerCase();
    const match = sites.find((s) => String(s.site_number || '').toLowerCase() === target);
    return match ? new Set([match.id]) : new Set();
  });
  useEffect(() => {
    if (!focusSiteNumber) return;
    const target = String(focusSiteNumber).toLowerCase();
    const match = sites.find((s) => String(s.site_number || '').toLowerCase() === target);
    if (!match) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-site-row="${match.id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'box-shadow 0.3s ease';
      el.style.boxShadow = '0 0 0 3px var(--color-warn-border, #fcd34d)';
      setTimeout(() => { el.style.boxShadow = ''; }, 2500);
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSiteNumber, sites.length]);
  // Per-customer price-increase dialog — aggregates across all sites'
  // subscriptions. Lives at the SitesTab header level so it isn't
  // repeated in every expanded row.
  const [priceEmailOpen, setPriceEmailOpen] = useState(false);
  // Per-site dialog (null when closed; site id when opened from a row's
  // 'Send Price Increase Email' button).
  const [priceEmailSiteId, setPriceEmailSiteId] = useState(null);
  // xsiteio onboarding wizard. Holds the site row Mary clicked
  // "Onboard to xsiteio" on; null = closed. Reuses the same dialog
  // for any site -- the dialog's localStorage state keeps per-site
  // checklist progress so re-opening picks up where she left off.
  const [onboardingSite, setOnboardingSite] = useState(null);
  // Per-row 'Sync to xsiteio' state. Just one site syncs at a time so
  // we use scalar state -- null when idle, the site.id while in flight.
  const [syncingSiteId, setSyncingSiteId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [pullingSiteId, setPullingSiteId] = useState(null);
  const [pullResult, setPullResult] = useState(null);
  // Per-site operational dialogs. State carries the site that the
  // button was clicked from so the dialog can prefill from that site.
  const [quoteSite, setQuoteSite] = useState(null);
  const [bullochSite, setBullochSite] = useState(null);
  // Per-site Send Email dialog. Opening it sets the site context so
  // template tokens like {{site_name}} / {{site_number}} resolve to
  // THIS site (not the customer-level merge fields). Dave 2026-06-24.
  const [emailSite, setEmailSite] = useState(null);
  // Dave 2026-06-12: parallel state for the PetroCan back-office
  // request. Same dialog component, just opened with type='petrocan'
  // so the server uses the PetroCan template + Jim Schroder recipient.
  const [petrocanSite, setPetrocanSite] = useState(null);
  const [parseSite, setParseSite] = useState(null);   // Bulloch reply parser, also per-site

  // Optimistic overrides for configuration flags so toggling a checkbox
  // updates the UI immediately while the network round-trip lands.
  // Keyed by configuration_id → { flag_key: bool }.
  const [flagOverrides, setFlagOverrides] = useState({});

  function toggleExpanded(siteId) {
    setExpandedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  // Pull every configuration row for a given site (configurations are
  // 1:1 with sites in practice, but the schema allows N — we render
  // all of them stacked just in case there's history).
  const configurationsBySite = (() => {
    const m = new Map();
    for (const c of configurations || []) {
      if (!m.has(c.site_id)) m.set(c.site_id, []);
      m.get(c.site_id).push(c);
    }
    return m;
  })();

  function configFlagValue(cfg, flagKey) {
    const o = flagOverrides[cfg.id];
    if (o && Object.prototype.hasOwnProperty.call(o, flagKey)) return o[flagKey];
    return !!cfg[flagKey];
  }

  async function toggleConfigFlag(cfg, flagKey) {
    const next = !configFlagValue(cfg, flagKey);
    setFlagOverrides((prev) => ({
      ...prev,
      [cfg.id]: { ...(prev[cfg.id] || {}), [flagKey]: next }
    }));
    try {
      await axios.put(`/api/bulloch/configuration/${cfg.id}`, { [flagKey]: next });
    } catch (err) {
      setFlagOverrides((prev) => ({
        ...prev,
        [cfg.id]: { ...(prev[cfg.id] || {}), [flagKey]: !next }
      }));
      console.warn('Flag toggle failed:', err.response?.data?.error || err.message);
    }
  }

  // Group credentials by site_id so each site can render its own stack.
  const credsBySiteId = (() => {
    const m = new Map();
    for (const c of credentials || []) {
      if (!m.has(c.site_id)) m.set(c.site_id, []);
      m.get(c.site_id).push(c);
    }
    return m;
  })();

  // Primary credential per site = lowest-id row. The SiteForm's inline
  // SFTP fields edit this one; additional credential sets live in the
  // per-row credential stack.
  const primaryBySiteId = (() => {
    const m = new Map();
    for (const c of credentials || []) {
      if (!m.has(c.site_id)) m.set(c.site_id, c);    // first encountered = lowest id
    }
    return m;
  })();

  function openAdd()                       { setEditing(null);   setFormOpen(true); }
  function openEdit(site)                  { setEditing(site);   setFormOpen(true); }
  function openCredAdd(site, e)            { e.stopPropagation(); setCredModalSite(site); setCredModalCred(null); setCredModalOpen(true); }
  function openCredEdit(site, cred, e)     { e.stopPropagation(); setCredModalSite(site); setCredModalCred(cred); setCredModalOpen(true); }

  async function handleTest(cred, e) {
    e.stopPropagation();
    setTestingCredId(cred.id);
    setTestResult(null);
    try {
      const res = await axios.post('/api/bulloch/test-sftp', { credential_id: cred.id });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        credential_id: cred.id,
        ok: false,
        error: err.response?.data?.error || err.message,
        response_time_ms: null
      });
    } finally {
      setTestingCredId(null);
      if (onSaved) onSaved();
    }
  }

  // Real-time site filter — matches across number, name, address,
  // city, and manager. Essential for multi-site customers (Centex = 85).
  const q = query.trim().toLowerCase();
  const filteredSites = !q ? sites : sites.filter((s) =>
    [s.site_number, s.site_name, s.address, s.city, s.site_manager]
      .some((v) => String(v || '').toLowerCase().includes(q))
  );

  return (
    <>
      <div className="tab-toolbar">
        <button type="button" className="btn-primary" onClick={openAdd}>
          + Add Site
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setPriceEmailOpen(true)}
          title="Aggregates the price-increase notice across every site this customer owns"
        >
          Send Price Increase Email
        </button>
      </div>

      {sites.length > 0 && (
        <div className="toolbar">
          <input
            type="search"
            className="search-input"
            placeholder="Search site number, name, address, city, manager..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="result-count">{filteredSites.length} of {sites.length}</span>
        </div>
      )}

      <PriceIncreaseEmailDialog
        open={priceEmailOpen}
        customerId={customerId}
        onClose={() => setPriceEmailOpen(false)}
        onSent={() => { if (onSaved) onSaved(); }}
      />

      <XsiteioOnboardingDialog
        open={onboardingSite != null}
        site={onboardingSite}
        customer={customer}
        subscription={onboardingSite ? (subscriptionsBySite || {})[onboardingSite.id] || null : null}
        onClose={() => setOnboardingSite(null)}
        onSaved={() => { if (onSaved) onSaved(); }}
      />

      <PriceIncreaseEmailDialog
        open={priceEmailSiteId != null}
        siteId={priceEmailSiteId}
        onClose={() => setPriceEmailSiteId(null)}
        onSent={() => { if (onSaved) onSaved(); }}
      />

      {testResult && (
        <p className={`sftp-test-banner sftp-test-${testResult.ok ? 'ok' : 'fail'}`}>
          {testResult.ok ? (
            <>
              <strong>Connected successfully</strong>
              {' '}to <code>{testResult.username}@{testResult.host}</code>
              {testResult.label && <> ({testResult.label})</>}
              {' '}in <strong>{testResult.response_time_ms} ms</strong>.
            </>
          ) : (
            <>
              <strong>Connection failed.</strong>
              {' '}{testResult.error || 'No error message returned.'}
              {testResult.response_time_ms != null && <> (after {testResult.response_time_ms} ms)</>}
            </>
          )}
        </p>
      )}

      {testResult?.ok && (
        <SftpFolderBrowser
          data={testResult}
          onRefreshed={(next) => setTestResult(next)}
        />
      )}

      {sites.length === 0 ? (
        <p className="empty-tab">No sites for this customer.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '28px' }} aria-label="Expand"></th>
                <th>Site #</th><th>Name</th><th>Renewal</th><th>Status</th>
                {/* Province re-added (Dave 2026-06-24 part 2) for an
                    upcoming territory-based project. The three
                    freshness signals follow it, then the xsiteio
                    Login (username) — kept next to Credentials since
                    they're the matched login surface for back-office
                    work. SFTP Credentials still last. */}
                <th>Province</th>
                <th>Last Login</th>
                <th>Last Pricebook Update</th>
                <th>Last Fuel &amp; Financial Done</th>
                <th>Login (xsiteio)</th>
                {/* Last Shift file Bulloch delivered via SFTP -- Dave
                    2026-07-09. Colored per lastShiftTone: green <=2d,
                    amber 3-5d, red >5d. If red, verify Bulloch's
                    config is still pointed correctly at our server. */}
                <th>Last Shift</th>
                <th>Credentials</th>
                <th aria-label="Ticket"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSites.length === 0 && (
                <tr><td colSpan={13} className="empty-row">No sites match “{query}”.</td></tr>
              )}
              {filteredSites.map((s) => {
                const siteCreds = credsBySiteId.get(s.id) || [];
                const isExpanded = expandedSiteIds.has(s.id);
                const subscription = (subscriptionsBySite || {})[s.id] || null;
                return (
                  <Fragment key={s.id}>
                  <tr
                    data-site-row={s.id}
                    onClick={() => toggleExpanded(s.id)}
                    className="row-clickable"
                  >
                    <td
                      className="cell-chevron"
                      aria-label={isExpanded ? 'Collapse site detail' : 'Expand site detail'}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </td>
                    <td className="cell-mono">{s.site_number || ''}</td>
                    <td><div className="cell-primary" style={subscription?.price_increase_email_sent ? { color: '#ED7D31', fontWeight: 'var(--font-bold)' } : undefined} title={subscription?.price_increase_email_sent ? `Price-increase email sent ${String(subscription.price_increase_email_sent).slice(0,10)}` : undefined}>{s.site_name || ''}</div></td>
                    <td className="cell-mono">{formatDate(s.renewal_date) || ''}</td>
                    <td><StatusBadge value={s.status} /></td>
                    {/* Province re-added 2026-06-24 part 2 for an
                        upcoming territory-based project. */}
                    <td>{s.province_state || ''}</td>
                    {/* Three xsiteio freshness signals -- Dave
                        2026-06-24. Address/City/Fuel/POS still on
                        the expanded panel below. */}
                    <td className="cell-mono" title={s.xsiteio_last_login || 'no data'}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8,
                        borderRadius: '50%', marginRight: 6,
                        background: xsiteioTone(s.xsiteio_last_login)
                      }} />
                      {formatRelative(s.xsiteio_last_login)}
                    </td>
                    <td className="cell-mono" title={s.xsiteio_last_updated || 'no data'}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8,
                        borderRadius: '50%', marginRight: 6,
                        background: xsiteioTone(s.xsiteio_last_updated)
                      }} />
                      {formatRelative(s.xsiteio_last_updated)}
                    </td>
                    {/* dd-mm-yyyy when the site subscribes to Fuel
                        Manager; "Not subscribed" badge when they
                        don't. Three distinct states so an upsell
                        candidate (not subscribed) never looks like a
                        stale subscriber (subscribed but dormant) --
                        see fmSubscriptionState above. */}
                    <td className="cell-mono" title={s.xsiteio_last_fuel_recon_at || 'no data'}>
                      {fmSubscriptionState(subscription) === 'unsubscribed' ? (
                        <span style={{
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 7px',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-mono)',
  fontSize: '10.5px',
  fontWeight: 'var(--font-semibold)',
  letterSpacing: 'var(--tracking-wide)',
  textTransform: 'uppercase',
  background: '#e5e7eb',
  color: '#4b5563',
  whiteSpace: 'nowrap'
}}>Not subscribed</span>
                      ) : (
                        <>
                          <span style={{
                            display: 'inline-block', width: 8, height: 8,
                            borderRadius: '50%', marginRight: 6,
                            background: xsiteioTone(s.xsiteio_last_fuel_recon_at)
                          }} />
                          {formatDDMMYYYY(s.xsiteio_last_fuel_recon_at)}
                        </>
                      )}
                    </td>
                    {/* xsiteio Login -- the customer's username to
                        log into the xsiteio web portal. Distinct
                        from the Last Login timestamp above. Dave
                        2026-06-24 needs this on the table for an
                        upcoming project. */}
                    <td className="cell-mono">
                      {s.xsite_io_username || <span className="cell-muted">—</span>}
                    </td>
                    {/* Last Shift received from Bulloch (via SFTP).
                        Colored dot + MM/DD/YY. Green (<=2d) = fresh;
                        amber (3-5d) = worth a look; red (>5d) means
                        Bulloch may have lost the SFTP config -- send
                        a support email to verify. Dave 2026-07-09. */}
                    <td className="cell-mono" title={s.xsiteio_last_shift_at || 'no data'}>
                      {s.xsiteio_last_shift_at ? (
                        <>
                          <span style={{
                            display: 'inline-block', width: 8, height: 8,
                            borderRadius: '50%', marginRight: 6,
                            background: lastShiftTone(s.xsiteio_last_shift_at)
                          }} />
                          {formatDDMMYYYY(s.xsiteio_last_shift_at)}
                        </>
                      ) : (
                        <span className="cell-muted">—</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="cred-stack">
                        {/* Display each credential as "username:password" plain
                            text. Test/Edit buttons stay on each row since
                            they're useful inline actions; the +Credential
                            button was removed because adding a credential is
                            now done via the SiteForm or the Pull-from-xsiteio
                            flow, not from the list cell (Dave 2026-06-11). */}
                        {siteCreds.length === 0 && (
                          <span className="cell-muted">—</span>
                        )}
                        {siteCreds.map((c) => {
                          const canTest = !!(c.host && c.username && c.password);
                          const busy = testingCredId === c.id;
                          const userPass = `${c.username || '—'}:${c.password || '—'}`;
                          return (
                            <div key={c.id} className="cred-card">
                              <div className="cred-card-head">
                                <span
                                  className="cred-card-label cell-mono"
                                  title={userPass}
                                  style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {userPass}
                                </span>
                              </div>
                              <div className="cred-card-actions">
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  onClick={(e) => handleTest(c, e)}
                                  disabled={busy || !canTest || testingCredId !== null}
                                  title={canTest ? 'Test SFTP Connection' : 'Host, username, and password are all required'}
                                >
                                  {busy ? 'Testing…' : 'Test'}
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  onClick={(e) => openCredEdit(s, c, e)}
                                >
                                  {String(c.label || '').toLowerCase() === 'petrocan' ? 'Edit / Create Dir' : 'Edit'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    {/* + Ticket — visible on every row without expanding.
                        Opens the New Ticket modal pre-filled with this
                        site's name + number. */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => setTicketSite(s)}
                      >
                        + Ticket
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="site-expanded-row" onClick={(e) => e.stopPropagation()}>
                      <td colSpan={12 /* keep in sync with header th count above */}>
                        {/* Per-site Support Team strip -- shows the CC
                            group contacts for THIS site's fuel_brand.
                            Multi-brand customers (one Esso + one
                            Chevron, etc.) get the right group per site
                            instead of a customer-wide fallback. Dave
                            2026-07-09. */}
                        <SupportTeamPanel customer={customer} site={s} />
                        {/* Per-site operational action bar — Generate
                            Quote and Send Bulloch Request live here
                            (moved off the customer-level header) so
                            each location gets its own action set. */}
                        <div className="site-actions-bar">
                          <button
                            type="button"
                            className="btn-primary btn-sm"
                            onClick={() => setQuoteSite(s)}
                            title="Generate a quote for this site"
                          >
                            Generate Quote
                          </button>
                          {/* Per-site Send Email — opens the same
                              dialog as the customer-level button at
                              the top of the page, but with the site
                              context so {{site_name}} / {{site_number}}
                              / {{site_address}} / {{site_city}} merge
                              tokens resolve to THIS site. Dave 2026-
                              06-24: needed for "email Centex about
                              one specific location" workflows. */}
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setEmailSite(s)}
                            title="Send an email about this specific site (uses site-aware merge tokens)"
                          >
                            Send Email
                          </button>
                          <a
                            className="btn-secondary btn-sm"
                            href={`${window.location.pathname}?tab=emails&email_site=${s.id}&email_cat=Support`}
                            title="Support emails about this site"
                          >
                            Support Emails
                          </a>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setBullochSite(s)}
                            title="Send a Bulloch Back Office configuration request for this site"
                          >
                            Send Bulloch Request
                          </button>
                          {/* PetroCan back-office request — only shown
                              for sites with a PetroCan fuel brand. Same
                              dialog as Bulloch, just opened with the
                              petrocan type so the server uses the
                              PetroCan template + Jim Schroder recipient. */}
                          {(() => {
                            const fb = String(s.fuel_brand || customer.fuel_brand || '').toLowerCase();
                            const isPetroCan = fb.includes('petro') || fb.includes('suncor');
                            if (!isPetroCan) return null;
                            return (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={() => setPetrocanSite(s)}
                                title="Send a PetroCan site update request to Jim Schroder at Suncor"
                              >
                                Send PetroCan Request
                              </button>
                            );
                          })()}
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setParseSite(s)}
                            title="Paste a Bulloch reply email to populate the SFTP configuration for this site"
                          >
                            Parse Bulloch Reply
                          </button>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => openEdit(s)}
                          >
                            Edit Site
                          </button>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setPriceEmailSiteId(s.id)}
                            title="Send a price-increase email for THIS site only and stamp this site"
                          >
                            Send Price Increase Email
                          </button>
                          {/* xsiteio onboarding wizard. Shows green
                              "Onboarded" if xsiteio_site_id is already
                              set; otherwise prompts to start the
                              wizard. Same dialog handles both flows
                              (resume mid-checklist from localStorage). */}
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setOnboardingSite(s)}
                            title="Step-by-step checklist to register this site in xsiteio"
                            style={s.xsiteio_site_id ? { borderColor: 'var(--color-good)', color: 'var(--color-good)' } : undefined}
                          >
                            {s.xsiteio_site_id ? '✓ xsiteio Onboarded' : 'Onboard to xsiteio'}
                          </button>
                          {/* ↑ Sync: push Command site details + ALL
                              subscription modules UP to xsiteio. Only
                              shows on linked sites. Idempotent. */}
                          {s.xsiteio_site_id && (
                            <button type="button" className="btn-secondary btn-sm"
                              onClick={async () => {
                                if (syncingSiteId) return;
                                setSyncingSiteId(s.id); setSyncResult(null);
                                try {
                                  const a = await axios.post(`/api/sites/${s.id}/xsiteio/create-site`);
                                  const b = await axios.post(`/api/sites/${s.id}/xsiteio/enable-billing`);
                                  setSyncResult({ site_id: s.id, ok: true,
                                    site_details: a.data?.already_done ? 'already done' : 'updated',
                                    enabled: b.data?.enabled ?? 0,
                                    skipped: b.data?.skipped ?? 0,
                                    failed:  b.data?.failed  ?? 0 });
                                } catch (err) {
                                  setSyncResult({ site_id: s.id, ok: false,
                                    error: err.response?.data?.error || err.message });
                                } finally { setSyncingSiteId(null); }
                              }}
                              disabled={syncingSiteId === s.id}
                              title="Push site details + ALL subscription modules to xsiteio (idempotent)"
                            >
                              {syncingSiteId === s.id ? 'Syncing…' : '↑ Sync to xsiteio'}
                            </button>
                          )}
                          {/* ↓ Pull: match this site to xsiteio (by
                              site_unique_id / digit fallback) and fill
                              in any blank local fields from xsiteio's
                              data. Shows on every site so unmatched
                              ones can resolve their xsiteio_site_id. */}
                          <button type="button" className="btn-secondary btn-sm"
                            onClick={async () => {
                              if (pullingSiteId) return;
                              setPullingSiteId(s.id); setPullResult(null);
                              try {
                                const r = await axios.post(`/api/sites/${s.id}/xsiteio/pull`);
                                // Log the full response (incl. diagnostic) so we can
                                // see what xsiteio actually returned when 'nothing extra'
                                // appears. Open DevTools console after clicking Pull.
                                console.log('[Pull from xsiteio] response for site', s.id, ':', r.data);
                                setPullResult({ site_id: s.id, ok: true,
                                  changed: r.data?.changed || {}, note: r.data?.note,
                                  diagnostic: r.data?.diagnostic });
                                onSaved && onSaved();
                              } catch (err) {
                                setPullResult({ site_id: s.id, ok: false,
                                  error: err.response?.data?.error || err.message });
                              } finally { setPullingSiteId(null); }
                            }}
                            disabled={pullingSiteId === s.id}
                            title="Match this site to xsiteio and fill in any data Command is missing"
                          >
                            {pullingSiteId === s.id ? 'Pulling…' : '↓ Pull from xsiteio'}
                          </button>
                          {/* Inline result chip shared by both Sync and Pull, whichever just ran. */}
                          {syncResult && syncResult.site_id === s.id && (
                            <span style={{
                              marginLeft: 8, padding: '2px 8px',
                              fontSize: 'var(--text-caption)', fontFamily: 'var(--font-mono)',
                              borderRadius: 'var(--radius-sm)',
                              background: syncResult.ok ? 'var(--color-good-bg)' : 'var(--color-bad-bg)',
                              color:      syncResult.ok ? 'var(--color-good)'    : 'var(--color-bad)'
                            }}>
                              {syncResult.ok
                                ? `✓ ${syncResult.enabled} enabled · ${syncResult.skipped} skipped · ${syncResult.failed} failed`
                                : `✗ ${(syncResult.error || '').slice(0, 80)}`}
                            </span>
                          )}
                          {pullResult && pullResult.site_id === s.id && (() => {
                            // Build a compact summary so a long changes list
                            // (e.g. 14 fields) doesn't blow out the row width
                            // and force the column to widen. Full list goes in
                            // the tooltip for when Dave needs the detail.
                            const changedKeys = Object.keys(pullResult.changed || {});
                            const shortLabel = pullResult.ok
                              ? (changedKeys.length === 0
                                  ? '✓ Nothing to pull'
                                  : changedKeys.length <= 2
                                    ? `✓ Pulled: ${changedKeys.join(', ')}`
                                    : `✓ Pulled ${changedKeys.length} field${changedKeys.length === 1 ? '' : 's'}`)
                              : `✗ ${(pullResult.error || '').slice(0, 60)}`;
                            const fullTip = pullResult.ok
                              ? (changedKeys.length
                                  ? `Pulled: ${changedKeys.join(', ')}`
                                  : '')
                                + (pullResult.note ? ` · ${pullResult.note}` : '')
                                + (pullResult.diagnostic ? ` · ${JSON.stringify(pullResult.diagnostic).slice(0, 400)}` : '')
                              : pullResult.error;
                            return (
                              <span style={{
                                marginLeft: 8, padding: '2px 8px',
                                fontSize: 'var(--text-caption)', fontFamily: 'var(--font-mono)',
                                borderRadius: 'var(--radius-sm)',
                                background: pullResult.ok ? 'var(--color-good-bg)' : 'var(--color-bad-bg)',
                                color:      pullResult.ok ? 'var(--color-good)'    : 'var(--color-bad)',
                                maxWidth: 220,
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                verticalAlign: 'middle',
                                cursor: 'help'
                              }}
                                title={fullTip}
                              >
                                {shortLabel}
                              </span>
                            );
                          })()}
                          {/* Hard-delete the site. Two-step confirm
                              because there is no soft-delete fallback -
                              once it goes, site_credentials and
                              configurations go with it via CASCADE. */}
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={async () => {
                              const label = s.site_name
                                || (s.site_number ? `#${s.site_number}` : `Site #${s.id}`);
                              if (!window.confirm(
                                `Delete site "${label}"?\n\nThis cannot be undone. ` +
                                `Credentials and configurations for this site will also be removed.`
                              )) return;
                              try {
                                await axios.delete(`/api/sites/${s.id}`);
                                onSaved && onSaved();
                              } catch (err) {
                                window.alert(`Delete failed: ${err.response?.data?.error || err.message}`);
                              }
                            }}
                            style={{ color: 'var(--color-bad)' }}
                            title="Hard-delete this site and its credentials"
                          >
                            Delete Site
                          </button>
                          {/* Result chips on their own row so a long
                              Pull changes list can't widen the button
                              row or push the right-column Save button
                              off-screen (Dave 2026-06-07). */}
                          {((syncResult && syncResult.site_id === s.id) ||
                            (pullResult && pullResult.site_id === s.id)) && (
                            <div className="site-actions-chip-row">
                              {syncResult && syncResult.site_id === s.id && (
                                <span style={{
                                  padding: '2px 8px',
                                  fontSize: 'var(--text-caption)', fontFamily: 'var(--font-mono)',
                                  borderRadius: 'var(--radius-sm)',
                                  background: syncResult.ok ? 'var(--color-good-bg)' : 'var(--color-bad-bg)',
                                  color:      syncResult.ok ? 'var(--color-good)'    : 'var(--color-bad)',
                                  maxWidth: 320,
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {syncResult.ok
                                    ? `✓ ${syncResult.enabled} enabled · ${syncResult.skipped} skipped · ${syncResult.failed} failed`
                                    : `✗ ${(syncResult.error || '').slice(0, 80)}`}
                                </span>
                              )}
                              {pullResult && pullResult.site_id === s.id && (() => {
                                const changedKeys = Object.keys(pullResult.changed || {});
                                const shortLabel = pullResult.ok
                                  ? (changedKeys.length === 0
                                      ? '✓ Nothing to pull'
                                      : changedKeys.length <= 2
                                        ? `✓ Pulled: ${changedKeys.join(', ')}`
                                        : `✓ Pulled ${changedKeys.length} field${changedKeys.length === 1 ? '' : 's'}`)
                                  : `✗ ${(pullResult.error || '').slice(0, 60)}`;
                                const fullTip = pullResult.ok
                                  ? (changedKeys.length ? `Pulled: ${changedKeys.join(', ')}` : '')
                                    + (pullResult.note ? ` · ${pullResult.note}` : '')
                                    + (pullResult.diagnostic ? ` · ${JSON.stringify(pullResult.diagnostic).slice(0, 400)}` : '')
                                  : pullResult.error;
                                return (
                                  <span style={{
                                    padding: '2px 8px',
                                    fontSize: 'var(--text-caption)', fontFamily: 'var(--font-mono)',
                                    borderRadius: 'var(--radius-sm)',
                                    background: pullResult.ok ? 'var(--color-good-bg)' : 'var(--color-bad-bg)',
                                    color:      pullResult.ok ? 'var(--color-good)'    : 'var(--color-bad)',
                                    maxWidth: 320,
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'help'
                                  }} title={fullTip}>
                                    {shortLabel}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        <div className="site-detail-split">
                          <div className="site-detail-left">
                            <div className="site-detail-head">
                              <h4 className="preview-heading" style={{ marginTop: 0 }}>
                                Site Detail
                              </h4>
                            </div>
                            <dl className="site-detail-list">
                              <div><dt>Site #</dt><dd className="cell-mono">{s.site_number || '—'}{' '}
                                <a className="site-emails-link" href={`${window.location.pathname}?tab=emails&email_site=${s.id}`} onClick={(e) => e.stopPropagation()}>Emails for this site</a>
                              </dd></div>
                              <div><dt>Site Name</dt><dd>{s.site_name || '—'}</dd></div>
                              <div><dt>Address</dt><dd>{s.address || '—'}</dd></div>
                              <div><dt>City</dt><dd>{s.city || '—'}</dd></div>
                              <div><dt>Province / State</dt><dd>{s.province_state || '—'}</dd></div>
                              <div><dt>Postal / Zip</dt><dd className="cell-mono">{s.postal_zip || '—'}</dd></div>
                              <div><dt>Fuel Brand</dt><dd>{s.fuel_brand || '—'}</dd></div>
                              <div><dt>POS Type</dt><dd>{s.pos_type ? <PosBadge value={s.pos_type} /> : '—'}</dd></div>
                              <div><dt>Status</dt><dd>{s.status ? <StatusBadge value={s.status} /> : '—'}</dd></div>
                              <div><dt>Install Date</dt><dd className="cell-mono">{formatDate(s.install_date) || '—'}</dd></div>
                              <div><dt>Renewal Date</dt><dd className="cell-mono">{formatDate(s.renewal_date) || '—'}</dd></div>
                              <div><dt>Site Phone</dt><dd className="cell-mono">{s.site_phone || '—'}</dd></div>
                              <div>
                                <dt>Site Manager</dt>
                                <dd className="site-manager-dd">
                                  <span>{s.site_manager || '—'}</span>
                                  {/* + Ticket — beside Site Manager, before the
                                      XSite IO + credentials fields. Opens a
                                      ticket pre-filled with this site. */}
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm site-ticket-inline"
                                    onClick={() => setTicketSite(s)}
                                  >
                                    + Ticket
                                  </button>
                                </dd>
                              </div>
                              <div>
                                <dt>Site Email</dt>
                                <dd>{s.site_email ? <a href={`mailto:${s.site_email}`}>{s.site_email}</a> : '—'}</dd>
                              </div>
                              <div><dt>XSite IO User</dt><dd className="cell-mono">{s.xsite_io_username || '—'}</dd></div>
                              <div><dt>XSite IO Password</dt><dd className="cell-mono">{s.xsite_io_password || '—'}</dd></div>
                              {/* xsiteio activity — populated by the
                                  nightly /api/1/all_site_informations sync.
                                  Dot colour: green ≤7d, amber ≤14d, red
                                  >14d, grey if we've never synced this site
                                  (xsiteio_site_id is NULL). The raw ISO
                                  timestamp is in the title tooltip so Mary
                                  can see the exact value if needed. */}
                              <div>
                                <dt>Last Login</dt>
                                <dd className="cell-mono" title={s.xsiteio_last_login || 'no data'}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: 8, height: 8, borderRadius: '50%',
                                      marginRight: 6,
                                      background: xsiteioTone(s.xsiteio_last_login)
                                    }}
                                  />
                                  {formatRelative(s.xsiteio_last_login)}
                                </dd>
                              </div>
                              <div>
                                <dt>Last Pricebook Update</dt>
                                <dd className="cell-mono" title={s.xsiteio_last_updated || 'no data'}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: 8, height: 8, borderRadius: '50%',
                                      marginRight: 6,
                                      background: xsiteioTone(s.xsiteio_last_updated)
                                    }}
                                  />
                                  {formatRelative(s.xsiteio_last_updated)}
                                </dd>
                              </div>
                              {/* Last Shift — sourced from xsiteio's
                                  last_fuel_reconciliation_date field
                                  (Dave 2026-06-24). Same colored-dot
                                  freshness convention as Last Login /
                                  Last Pricebook Update so a site that
                                  hasn't closed a shift in 2+ weeks
                                  jumps out red. Title carries the raw
                                  ISO timestamp for exact lookup. */}
                              <div>
                                <dt>Last Fuel &amp; Financial Done</dt>
                                <dd className="cell-mono" title={s.xsiteio_last_fuel_recon_at || 'no data'}>
                                  {fmSubscriptionState(subscription) === 'unsubscribed' ? (
                                    <span style={{
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 7px',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-mono)',
  fontSize: '10.5px',
  fontWeight: 'var(--font-semibold)',
  letterSpacing: 'var(--tracking-wide)',
  textTransform: 'uppercase',
  background: '#e5e7eb',
  color: '#4b5563',
  whiteSpace: 'nowrap'
}}>Not subscribed</span>
                                  ) : (
                                    <>
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          width: 8, height: 8, borderRadius: '50%',
                                          marginRight: 6,
                                          background: xsiteioTone(s.xsiteio_last_fuel_recon_at)
                                        }}
                                      />
                                      {formatDDMMYYYY(s.xsiteio_last_fuel_recon_at)}
                                    </>
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt>Last Purchase Order Created</dt>
                                <dd className="cell-mono" title={s.xsiteio_last_po_created_at || 'no data'}>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, background: xsiteioTone(s.xsiteio_last_po_created_at) }} />
                                  {formatDDMMYYYY(s.xsiteio_last_po_created_at) || '—'}
                                </dd>
                              </div>
                              <div>
                                <dt>Last Shift Date</dt>
                                <dd className="cell-mono" title={s.xsiteio_last_shift_at || 'no data'}>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, background: xsiteioTone(s.xsiteio_last_shift_at) }} />
                                  {formatDDMMYYYY(s.xsiteio_last_shift_at) || '—'}
                                </dd>
                              </div>
                            </dl>
                            <SiteEmails site={s} />
                          </div>
                          <div className="site-detail-right">
                            <SiteSubscriptionPanel
                              siteId={s.id}
                              subscription={subscription}
                              province={s.province_state}
                              renewalDate={s.renewal_date}
                              onSaved={onSaved}
                            />
                          </div>
                        </div>

                        {/* Configuration flags — Bulloch SFTP data-feed
                            booleans for this site. Lives below the
                            split panel because it's operational
                            chrome, not editing UI for the site row
                            itself. */}
                        <SiteConfigurationPanel
                          configurations={configurationsBySite.get(s.id) || []}
                          flagValue={configFlagValue}
                          onToggleFlag={toggleConfigFlag}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <SiteForm
        open={formOpen}
        customerId={customerId}
        site={editing}
        primaryCredential={editing ? primaryBySiteId.get(editing.id) : null}
        parentRecord={customer}
        onClose={() => setFormOpen(false)}
        onSaved={() => { if (onSaved) onSaved(); }}
      />

      <NewTicketModal
        open={!!ticketSite}
        preselectedSiteId={ticketSite?.id}
        currentUserId={user?.id}
        onCancel={() => setTicketSite(null)}
        onCreated={() => setTicketSite(null)}
      />

      <CredentialForm
        open={credModalOpen}
        site={credModalSite}
        credential={credModalCred}
        onClose={() => setCredModalOpen(false)}
        onSaved={() => { if (onSaved) onSaved(); }}
      />

      {/* Per-site operational dialogs. Each carries `site` so the
          dialog can prefill from site-level operational fields and
          tell the server which site_id to merge against. The
          customer record stays as the billing contact. */}
      <QuoteDialog
        open={!!quoteSite}
        record={customer}
        site={quoteSite}
        onClose={() => setQuoteSite(null)}
      />
      {/* Per-site Send Email -- same SendEmailDialog as the customer-
          level button, but with siteContext set so {{site_name}} etc.
          merge tokens resolve. Sites array is set to [emailSite] so
          {{site_list}} renders just that one location. Dave 2026-06-24. */}
      <SendEmailDialog
        open={!!emailSite}
        record={customer}
        recordType="customer"
        sites={emailSite ? [emailSite] : null}
        siteContext={emailSite}
        onClose={() => setEmailSite(null)}
        onSent={() => { if (onSaved) onSaved(); }}
      />
      <BullochConfigDialog
        open={!!bullochSite}
        record={customer}
        site={bullochSite}
        onClose={() => setBullochSite(null)}
        onSent={onSaved}
      />
      {/* Second mount of the same dialog for PetroCan sends. type prop
          switches the server to the PetroCan template + Jim Schroder
          recipient. Dave 2026-06-12. */}
      <BullochConfigDialog
        open={!!petrocanSite}
        record={customer}
        site={petrocanSite}
        type="petrocan"
        onClose={() => setPetrocanSite(null)}
        onSent={onSaved}
      />
      <BullochEmailParseDialog
        open={!!parseSite}
        record={customer}
        site={parseSite}
        onClose={() => setParseSite(null)}
        onSaved={() => { if (onSaved) onSaved(); }}
      />
    </>
  );
}

// Inline configuration-flags panel rendered inside the expanded Site
// row. Lists every configuration record for the site (almost always
// one) and shows the 10 Bulloch data-feed flags as a row of
// optimistic-toggle checkboxes. When no configuration exists, prompts
// the user toward the Parse Bulloch Reply button.
function SiteConfigurationPanel({ configurations, flagValue, onToggleFlag }) {
  if (!configurations || configurations.length === 0) {
    return (
      <div className="site-config-panel site-config-panel-empty">
        <h4 className="preview-heading" style={{ margin: 0 }}>Configuration Flags</h4>
        <p className="cell-muted" style={{ margin: 0 }}>
          No SFTP configuration on file for this site. Use
          {' '}<strong>Parse Bulloch Reply</strong> above once you receive
          Bulloch's response.
        </p>
      </div>
    );
  }
  return (
    <div className="site-config-panel">
      <div className="site-config-head">
        <h4 className="preview-heading" style={{ margin: 0 }}>Configuration Flags</h4>
      </div>
      {configurations.map((cfg) => (
        <div key={cfg.id} className="site-config-row">
          <div className="site-config-meta">
            <StatusBadge value={cfg.status} />
            <span className="cell-mono">{formatDate(cfg.date_received) || 'No date'}</span>
            {cfg.sftp_host && <span className="cell-mono cell-muted">{cfg.sftp_username || ''}@{cfg.sftp_host}{cfg.sftp_port ? `:${cfg.sftp_port}` : ''}</span>}
          </div>
          <div className="site-config-flags">
            {CONFIG_FLAGS.map((f) => (
              <label key={f.key} className="config-flag-check" title={f.full}>
                <input
                  type="checkbox"
                  checked={flagValue(cfg, f.key)}
                  onChange={() => onToggleFlag(cfg, f.key)}
                  aria-label={f.full}
                />
                <span>{f.short}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NotesTab({ customerId, notes, onAdded }) {
  const initialForm = () => ({
    note_date: new Date().toISOString().slice(0, 10),
    type: 'Phone',
    text: ''
  });
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.text.trim()) {
      setError('Note text is required.');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/notes', {
        contact_id: Number(customerId),
        note_date: form.note_date,
        type: form.type,
        text: form.text
      });
      setForm(initialForm());
      await onAdded();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="notes-panel">
      <form onSubmit={handleSubmit} className="note-form">
        <h3>Add Note</h3>
        {error && <p className="error">{error}</p>}
        <div className="note-form-row">
          <label>
            <span>Type</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option>Phone</option>
              <option>Email</option>
              <option>Meeting</option>
              <option>Demo</option>
              <option>Other</option>
            </select>
          </label>
          <label>
            <span>Date</span>
            <DateInput value={form.note_date} onChange={(e) => setForm({ ...form, note_date: e.target.value })} />
          </label>
        </div>
        <textarea
          rows={3}
          placeholder="Note text…"
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
        />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </form>

      <h3>History ({notes.length})</h3>
      {notes.length === 0 && <p className="empty-tab">No notes yet.</p>}
      <ul className="note-list">
        {notes.map((n) => {
          const typeClass = `note-type-${(n.type || 'other').toLowerCase()}`;
          return (
            <li key={n.id} className={`note-item ${typeClass}`}>
              <div className="note-meta">
                <Badge tone={noteTone(n.type)}>{n.type || 'Other'}</Badge>
                <span>{formatDate(n.note_date)}{n.note_time ? ` · ${formatTime(n.note_time)}` : ''}</span>
                {n.user_name && <span>· {n.user_name}</span>}
                <EmailOpenBadge note={n} />
              </div>
              <div className="note-text">{n.text}</div>
              {/* Attachments captured from inbound forwarded emails. The
                  /api/notes endpoint pre-counts these so we can show a
                  paperclip without an extra fetch -- the list itself is
                  lazy-fetched when expanded. */}
              {Number(n.attachment_count) > 0 && <NoteAttachments noteId={n.id} count={n.attachment_count} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Inline attachment list shown under a note when attachment_count > 0.
// Collapsed by default: a small paperclip + count pill that expands
// when clicked. Files are fetched lazily so notes without attachments
// (the vast majority) never trigger a request.
//
// Clicking a filename opens the download URL in a new tab; PDFs and
// images render inline (Content-Disposition: inline on the server),
// Office docs come down as a normal download.
function NoteAttachments({ noteId, count }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && items === null && !loading) {
      setLoading(true);
      try {
        const r = await axios.get(`/api/notes/${noteId}/attachments`);
        setItems(r.data?.attachments || []);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  }

  function fmtSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      <button
        type="button"
        onClick={toggle}
        className="btn-secondary btn-sm"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        title={open ? 'Hide attachments' : 'Show attachments'}
      >
        <span aria-hidden>📎</span>
        <span>{count} attachment{count === 1 ? '' : 's'}</span>
        <span style={{ opacity: 0.6 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-4)' }}>
          {loading && <div className="result-count">Loading…</div>}
          {error   && <div className="error">{error}</div>}
          {!loading && Array.isArray(items) && items.length === 0 && (
            <div className="result-count">No attachments found.</div>
          )}
          {!loading && Array.isArray(items) && items.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 2 }}>
              <a
                href={`/api/notes/${noteId}/attachments/${a.id}/download`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--color-accent, #0e7490)', fontWeight: 'var(--font-semibold)', textDecoration: 'none' }}
                title={`Open ${a.filename}`}
              >
                {a.filename || `attachment-${a.id}`}
              </a>
              <span style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                {a.content_type || ''} · {fmtSize(a.size_bytes)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Per-contact inline editor. Renders as a "view" row by default
// (display-only chips with an Edit button); clicking Edit swaps to an
// input-row layout. The same component handles "draft" mode for the
// + Add Contact button — when `draft` is true the row starts open
// without an underlying id, and cancelling discards it.
// ContactRow + ContactsTab used to live here. They moved to the shared
// ContactsSection component (client/src/components/ContactsSection.jsx)
// so the same inline editor renders below the contact card on both
// Customer and Lead detail pages.

function DocumentsTab({ items }) {
  // Re-print state per-row so multiple downloads in parallel don't
  // step on each other. Map of document_id -> 'busy' | null.
  const [reprintBusy, setReprintBusy] = useState({});
  const [reprintError, setReprintError] = useState(null);
  // Which email document is currently open in the preview modal.
  const [previewDocId, setPreviewDocId] = useState(null);

  async function handleReprint(doc) {
    setReprintError(null);
    setReprintBusy((s) => ({ ...s, [doc.id]: true }));
    try {
      // Snapshot vs live: server reports via X-Reprint-Mode header. If
      // it's 'live', warn the user that this won't match the original
      // exactly. Confirm before triggering the actual download.
      const hasSnapshot = !!doc.merge_snapshot;
      if (!hasSnapshot) {
        const ok = window.confirm(
          `No stored snapshot for this quote (sent ${doc.sent_at ? new Date(doc.sent_at).toLocaleDateString() : 'date unknown'}).\n\n` +
          'Re-printing will use TODAY\'s customer + subscription data, ' +
          'so prices, line items, or contact info may differ from the original. ' +
          'Continue?'
        );
        if (!ok) {
          setReprintBusy((s) => ({ ...s, [doc.id]: false }));
          return;
        }
      }
      const res = await axios.post(`/api/quotes/${doc.id}/regenerate`, {}, {
        responseType: 'blob'
      });
      // Pull filename from Content-Disposition; fall back if missing.
      const disp = res.headers['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m ? m[1] : `REPRINT_quote_${doc.id}.pdf`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setReprintError(`Re-print #${doc.id}: ${msg}`);
    } finally {
      setReprintBusy((s) => ({ ...s, [doc.id]: false }));
    }
  }

  if (items.length === 0) return <p className="empty-tab">No documents generated yet.</p>;
  return (
    <div className="table-wrap">
      {reprintError && <p className="error">{reprintError}</p>}
      <table className="data-table">
        <thead>
          <tr>
            <th>Type</th><th>File</th><th>Created</th><th>Sent</th>
            <th>Open Status</th><th>First Opened</th><th aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => {
            const isQuote = (d.type || '').toLowerCase() === 'quote';
            const busy = !!reprintBusy[d.id];
            const hasSnapshot = !!d.merge_snapshot;
            return (
              <tr key={d.id}>
                <td>
                  {d.type || ''}
                  {isQuote && (
                    <span
                      title={hasSnapshot ? 'Snapshot stored — re-print is faithful' : 'No snapshot — re-print uses today\'s data'}
                      style={{
                        display: 'inline-block', width: 8, height: 8,
                        borderRadius: '50%', marginLeft: 6,
                        background: hasSnapshot ? '#047857' : '#b45309'
                      }}
                    />
                  )}
                </td>
                <td className="cell-mono">{d.file_path || ''}</td>
                <td className="cell-mono">{formatDateTime(d.created_at)}</td>
                <td className="cell-mono">{formatDateTime(d.sent_at)}</td>
                <td><OpenStatusBadge document={d} /></td>
                <td className="cell-mono">{formatDateTime(d.opened_at)}</td>
                <td>
                  {isQuote && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => handleReprint(d)}
                      disabled={busy}
                      title={hasSnapshot
                        ? 'Re-print this quote (faithful copy from stored snapshot)'
                        : 'Re-print this quote (uses TODAY\'s data — may not match original)'}
                    >
                      {busy ? 'Regenerating…' : 'Re-print'}
                    </button>
                  )}
                  {/* View button: works on any document that has a
                      resend_message_id (Emails, Bulloch Requests, and
                      even Quotes -- Quotes were sent as an email with
                      the PDF attached, so the email body is worth
                      seeing). Dave 2026-07-09. */}
                  {d.resend_message_id && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      style={{ marginLeft: isQuote ? 6 : 0 }}
                      onClick={() => setPreviewDocId(d.id)}
                      title="View the sent email content"
                    >
                      View
                    </button>
                  )}
                </td>
              </tr>            );
          })}
        </tbody>
      </table>
      {previewDocId && (
        <EmailPreviewModal
          documentId={previewDocId}
          onClose={() => setPreviewDocId(null)}
        />
      )}
    </div>
  );
}

// The 10 boolean flag columns in the order they're stored on the
// configurations table. Short labels keep the table compact; the full
// names are surfaced via the column tooltip.
const CONFIG_FLAGS = [
  { key: 'individual_trx',       short: 'Indiv Trx',       full: 'Individual Transactions' },
  { key: 'customer_sales_xml',   short: 'Cust Sales',      full: 'Customer Sales XML' },
  { key: 'shift_xml',            short: 'Shift XML',       full: 'Shift XML' },
  { key: 'all_data_shift_xml',   short: 'All Data Shift',  full: 'All Data Shift XML' },
  { key: 'shift_report',         short: 'Shift Rpt',       full: 'Shift Report' },
  { key: 'fuel_price_changes',   short: 'Fuel Price',      full: 'Fuel Price Changes' },
  { key: 'price_surveys',        short: 'Price Surv',      full: 'Price Surveys' },
  { key: 'ixretail',             short: 'ixRetail',        full: 'ixRetail' },
  { key: 'nacs_xml',             short: 'NACS XML',        full: 'NACS XML' },
  { key: 'html_shift_receipts',  short: 'HTML Recpts',     full: 'HTML Shift Receipts' }
];

// ConfigurationsTab was removed when the standalone Configuration tab
// was retired — the flag-toggle UI now lives inline on each Site row
// (see SiteConfigurationPanel above), keyed against the same
// /api/bulloch/configuration/:id endpoint.
